import React, { useState, useEffect } from 'react';
import { Layers, FileDown } from 'lucide-react';
import { Ks6aEntry } from '../../types';
import { exportKs6aToDocx } from '../../utils/exportDocx';

export const Ks6aLedger: React.FC = () => {
  const [entries, setEntries] = useState<Ks6aEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/pto/ks6a')
      .then(r => r.json())
      .then(d => {
        if (d.success) setEntries(d.data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <span className="p-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-xl">
              <Layers className="w-5 h-5" />
            </span>
            <span>Накопительная ведомость учета СМР (КС-6а)</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Сквозной контроль: Проектный объем ➔ Факт прораба ➔ Оформлено в АОСР ➔ Подписано Технадзором
          </p>
        </div>

        <button
          onClick={() => exportKs6aToDocx(entries, 'ЖК «Северная Звезда»')}
          disabled={loading || entries.length === 0}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 transition self-start sm:self-auto disabled:opacity-50 active:scale-95"
        >
          <FileDown className="w-4 h-4" />
          <span>Скачать в Word (.docx)</span>
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-400 text-sm">Загрузка ведомости КС-6а...</div>
      ) : (
        <div className="bg-white border border-slate-200/90 rounded-2xl overflow-x-auto shadow-sm">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-semibold">
                <th className="p-3">Зона / Захватка</th>
                <th className="p-3">Вид электромонтажа</th>
                <th className="p-3 text-right">План проекта</th>
                <th className="p-3 text-right text-amber-700">Факт прораба</th>
                <th className="p-3 text-right text-blue-700">В Актах АОСР</th>
                <th className="p-3 text-right text-emerald-700">Подписано Технадзором</th>
                <th className="p-3 text-right">Готовность</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-800">
              {entries.map((item) => {
                const progressPct = item.projectPlannedVolume > 0
                  ? Math.min(100, Math.round((item.foremanFactVolume / item.projectPlannedVolume) * 100))
                  : 0;

                return (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition">
                    <td className="p-3">
                      <div className="font-bold text-slate-900">{item.zone_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{item.block_name} • {item.zone_code}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-slate-800">{item.work_type_name}</div>
                      <div className="text-[10px] text-slate-400">{item.work_type_code}</div>
                    </td>
                    <td className="p-3 text-right font-semibold text-slate-700">{item.projectPlannedVolume} {item.unit}</td>
                    <td className="p-3 text-right font-black text-amber-700">{item.foremanFactVolume} {item.unit}</td>
                    <td className="p-3 text-right font-black text-blue-700">{item.ptoAosrVolume} {item.unit}</td>
                    <td className="p-3 text-right font-black text-emerald-700">{item.techsupSignedVolume} {item.unit}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-slate-200 h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${progressPct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        <span className="font-bold text-xs text-slate-900">{progressPct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
